import React from 'react';
import { store } from '../redux/store';
import request from '.';
import Exported from './endpoints';
import dayjs from 'dayjs';
import { FIELD_OFFICER, GAUGE_COLORS } from '../constants/constants';
import config from './env';
import axios from 'axios';

export const setAuthToken = (token) => {
  return localStorage.setItem('accessToken', token);
};

export const getTokenFromStore = () => {
  const state = store.getState();
  const token = state.auth.accessToken;
  return token;
};

export const logout = () => {
  localStorage.clear();
  setTimeout(() => {
    globalThis.window.location.pathname = '/';
  }, 200);
};

export const sanitizePayload = (data, options = {}) => {
  const { keepEmptyString = false } = options;

  return Object.keys(data).reduce((acc, key) => {
    const value = data[key];

    if (value === undefined) {
      acc[key] = null;
    } else if (value === '' && !keepEmptyString) {
      acc[key] = null;
    } else {
      acc[key] = value;
    }

    return acc;
  }, {});
};

export const capitalizeFirstLetter = (str) => {
  if (typeof str !== 'string') {
    return str;
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const capitalizeEachWord = (str) => {
  if (typeof str !== 'string') {
    return str;
  }
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const isEqualDeep = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export const normalizeEmptyToNull = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(normalizeEmptyToNull);
  }

  if (obj instanceof Date || dayjs.isDayjs(obj)) {
    return dayjs(obj).isValid() ? dayjs(obj).format('YYYY-MM-DD') : null;
  }

  if (obj && typeof obj === 'object') {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      acc[key] = value === undefined || value === '' ? null : normalizeEmptyToNull(value);
      return acc;
    }, {});
  }

  return obj;
};

export const capitalizeObjectValues = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(capitalizeObjectValues); // Recurse for each item in the array
  } else if (typeof obj === 'object' && obj !== null) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, capitalizeObjectValues(value)])
    );
  } else {
    return capitalizeFirstLetter(obj); // Capitalize only string values
  }
};

export const removeUnderScoreAndCapitalize = (str) => {
  if (typeof str !== 'string') {
    return str;
  }

  return str
    .replace(/_/g, ' ') // convert underscores to spaces
    .split(/\s+/) // split by one or more spaces
    .filter(Boolean) // remove empty parts
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const maskEmail = (email) => {
  const [localPart, domain] = email.split('@');
  const maskedLocalPart = `${localPart.slice(0, 2)}${'*'.repeat(localPart.length - 2)}`;
  return `${maskedLocalPart}@${domain}`;
};

export const maskMobile = (mobile) => {
  const visibleStart = mobile.slice(0, 2);
  const visibleEnd = mobile.slice(-2);
  const masked = '*'.repeat(mobile.length - 4);
  return `${visibleStart}${masked}${visibleEnd}`;
};

export const getUniqueOptions = (data = [], key = 'value') => {
  return Array.from(
    new Map(data.filter((item) => item?.[key] != null).map((item) => [item[key], item])).values()
  );
};

export const formatVideoDuration = (durationInSeconds) => {
  if (typeof Intl.DurationFormat === 'function') {
    const duration = {
      hours: Math.floor(durationInSeconds / 3600),
      minutes: Math.floor((durationInSeconds % 3600) / 60),
      seconds: Math.floor(durationInSeconds % 60),
    };
    const formatter = new Intl.DurationFormat('en', { style: 'long' });
    return formatter.format(duration);
  } else {
    const totalSeconds = Math.floor(durationInSeconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    let formattedDuration = '';
    if (hours > 0) {
      formattedDuration += `${hours}h `;
    }
    if (minutes > 0) {
      formattedDuration += `${minutes}m `;
    }
    formattedDuration += `${seconds}s`;

    return formattedDuration.trim();
  }
};

export const singleUpload = async (uploadedFiles) => {
  const formData = new FormData();
  const durations = [];
  const slideCounts = [];
  uploadedFiles.forEach((file) => {
    formData.append('files', file);
    durations.push(file?.duration || '1');
    slideCounts.push(file?.pages || '1');
  });

  formData.append('duration', durations.join(','));
  formData.append('pdfPageCount', slideCounts.join(','));
  const fileUploads = [];
  await request({
    url: Exported.Endpoints.uploadfile,
    method: Exported.APIMethods.POST,
    data: formData,
    headers: {
      'content-type': 'multipart/form-data',
    },
    baseUrl: config.documentServiceApi,
  })
    .then((res) => {
      fileUploads?.push(res?.data?.successRes);
    })
    .catch((err) => {
      console.error(err);
    });
  return fileUploads?.flat();
};

export const largeFileUpload = async (uploadedFiles, onProgress, signal) => {
  const formData = new FormData();
  const durations = [];
  const slideCounts = [];
  uploadedFiles.forEach((file) => {
    formData.append('files', file);
    durations.push(file?.duration || '1');
    slideCounts.push(file?.pages || '1');
  });

  formData.append('duration', durations.join(','));
  formData.append('pdfPageCount', slideCounts.join(','));
  const fileUploads = [];
  await request({
    url: Exported.Endpoints.uploadfile,
    method: Exported.APIMethods.POST,
    data: formData,
    headers: {
      'content-type': 'multipart/form-data',
    },
    baseUrl: config.documentServiceApi,
    onUploadProgress: onProgress,
    timeout: 0, // No timeout for large uploads
    signal,
  })
    .then((res) => {
      fileUploads?.push(res?.data?.successRes);
    })
    .catch((err) => {
      console.error(err);
    });
  return fileUploads?.flat();
};

/**
 * Presigned video upload flow:
 * 1. POST /file/upload/presigned with file metadata → get { uploadUrl, fileUrl }
 * 2. PUT to uploadUrl with raw file binary (no custom headers — S3 presigned URL
 *    is signed only for specific headers; adding extras breaks the sig and causes CORS errors)
 * 3. Return fileUrl to be used in the submit API payload
 */
export const presignedVideoUpload = async (file, onProgress, signal) => {
  // Step 1: Get presigned URL from our backend
  const presignedRes = await request({
    url: '/file/upload/presigned',
    method: Exported.APIMethods.POST,
    data: {
      originalName: file.name,
      mimeType: file.type || 'video/mp4',
      isPublic: true,
      folder: 'videos/uploads',
    },
    baseUrl: config.documentServiceApi,
    signal,
  });

  const { uploadUrl, fileUrl, key } = presignedRes?.data || {};

  if (!uploadUrl || !fileUrl) {
    throw new Error('Failed to get presigned upload URL from server.');
  }

  // Step 2: PUT raw file binary directly to S3
  // We use axios to directly send the File object as the body, exactly matching Postman's binary mode
  try {
    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': file.type || 'video/mp4',
        'Content-Disposition': `inline; filename="${file.name}"`,
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) onProgress(progressEvent);
      },
      signal,
    });
  } catch (error) {
    if (axios.isCancel(error) || error.name === 'CanceledError') {
      throw new DOMException('Upload aborted', 'AbortError');
    }
    // If it fails here, it is almost certainly a CORS error from S3 due to missing AllowedHeaders
    throw new Error(
      'S3 PUT network error. Check CORS config on bucket. Please ensure <AllowedHeader>Content-Disposition</AllowedHeader> is added to the S3 Bucket CORS policy.'
    );
  }

  // Step 3: Return fileUrl for use in the submit payload
  return { fileUrl, key };
};

export const getPublicURL = async (file, fileKey) => {
  let fileURL = file;
  await request({
    url: `${Exported.Endpoints.getUploadfile}/${fileKey}`,
    method: Exported.APIMethods.GET,
    baseUrl: config.documentServiceApi,
  })
    .then((res) => {
      fileURL = res?.data?.url;
    })
    .catch((err) => {
      console.error(err);
    });
  return fileURL;
};

export const gets3Url = async (file, fileKey) => {
  const url = await getPublicURL(file, fileKey);
  return url;
};

export const getFileType = (fileName) =>
  fileName?.includes('.') ? fileName.split('.').pop().toLowerCase() : null;

export const capitalizeString = (str = '') => (str ? str[0].toUpperCase() + str.slice(1) : '');

export const getLookupOptions = (filterConfigs, lookupData) => {
  return filterConfigs.map((filterConfig) => {
    let list = [];
    // If filter already has a static list, use it; otherwise fetch from lookupData
    if (filterConfig.list) {
      list = filterConfig.list;
    } else if (lookupData?.[filterConfig.type]) {
      list = lookupData[filterConfig.type].map((item) => ({
        label: item.label,
        value: item.value,
        selected: false,
      }));
    }
    return {
      ...filterConfig,
      list,
    };
  });
};

export const getTruncatedMessage = (eventName) => {
  const maxLength = 20;
  const truncatedName =
    eventName.length > maxLength ? `${eventName.slice(0, maxLength)}...` : eventName;

  return truncatedName;
};

export const getLookUpLabel = (mod, key, lookupData) => {
  let moduleName = mod;
  if (lookupData?.[key]) {
    moduleName = lookupData?.[key]?.filter((x) => x?.value == mod)?.at(0)?.label || mod;
  }
  return moduleName;
};

export const getStateLabel = (mod, stateList) => {
  let moduleName = mod;
  if (stateList) {
    moduleName = stateList?.filter((x) => x?.value == mod)?.at(0)?.label || mod;
  }
  return moduleName;
};

export const formatLabel = (text) => {
  return text.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

export const formatProofOfOwnershipLabel = (status) => {
  if (status === 'completed') {
    return 'Yes';
  }
  if (status === 'not_completed') {
    return 'No';
  }
  return 'Pending';
};

export const formatOwnershipLabel = (text = '') => {
  return text
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const formatNumber = (num) => {
  if (Number.isFinite(num) && !Number.isInteger(num)) {
    return Number(num.toFixed(2));
  }
  // Ensure output is a number
  return Number(num) ? Number(num) : 0;
};

export const nullHandleCardData = (value) => {
  if (value === null || value === undefined || value === '') {
    return '0';
  }

  // Check if it's a number
  if (typeof value === 'number') {
    // Apply toFixed only if decimal exists
    return Number.isInteger(value) ? value : value.toFixed(2);
  }

  return value;
};

export const formatAnalyticsValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '0';
  }

  const num = Number(value);

  if (Number.isNaN(num)) {
    return String(value);
  }

  // For numbers >= 10000, use K/M/B format
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(2) + 'B';
  } else if (num >= 1000000) {
    return (num / 1000000).toFixed(2) + 'M';
  } else if (num >= 10000) {
    return (num / 1000).toFixed(2) + 'K';
  }

  // For numbers with decimals, use toFixed(2)
  if (!Number.isInteger(num)) {
    return num.toFixed(2);
  }

  // For integers less than 10000, return as is
  return num.toString();
};

export const formatDateToYYYYMMDD = (date) => {
  return dayjs(date).format('YYYY-MM-DD');
};
export const formatDateDDMMYYYY = (dateString) => {
  if (dateString === null || dateString === undefined) return '-';
  return dayjs(dateString).format('DD-MM-YYYY');
};

export function formatDateToYYYYMMDDNullHandled(dateValue) {
  if (!dateValue) return '';

  const date = new Date(dateValue);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export const formatDate = (year, month, day) => {
  const paddedMonth = String(month + 1).padStart(2, '0');
  const paddedDay = String(day).padStart(2, '0');
  return `${year}-${paddedMonth}-${paddedDay}`;
};

export function getMonthFromDate(dateString) {
  if (dateString === null || dateString === undefined) return '-';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      console.error('Invalid date string provided:', dateString);
      return null;
    }
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    return monthNames[date.getMonth()];
  } catch (error) {
    console.error('Error parsing date string:', error);
    return null;
  }
}

export const isFieldDisabled = (initialData, fieldName, view = false, isEditPage = false) => {
  if (isEditPage) {
    return false;
  }
  if (view) {
    return true;
  }
  const val = initialData?.[fieldName];
  return val !== undefined && val !== null && val !== '';
};

export const isFieldOfficer = (role) => {
  const roles = [FIELD_OFFICER];
  return !roles.includes(role);
};

export function deepCleanObject(obj) {
  if (Array.isArray(obj)) {
    const cleanedArray = obj
      .map(deepCleanObject)
      .filter(
        (item) => item !== null && !(typeof item === 'object' && Object.keys(item).length === 0)
      );

    return cleanedArray.length > 0 ? cleanedArray : null;
  }

  if (typeof obj === 'object' && obj !== null) {
    const cleanedObj = Object.entries(obj).reduce((acc, [key, value]) => {
      const cleanedValue = deepCleanObject(value);
      if (
        cleanedValue !== null &&
        cleanedValue !== '' &&
        !(typeof cleanedValue === 'object' && Object.keys(cleanedValue).length === 0)
      ) {
        acc[key] = cleanedValue;
      }
      return acc;
    }, {});

    return Object.keys(cleanedObj).length > 0 ? cleanedObj : null;
  }

  return obj === '' ? null : obj;
}

export const getFileTypeFomat = (mimeType = '') => {
  const map = {
    'application/pdf': 'pdf',
    'image/jpeg': 'jpeg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-excel': 'xls',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'video/mp4': 'mp4',
    'application/vnd.ms-powerpoint': 'ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
    'text/csv': 'csv',
  };

  return map[mimeType] || ''; // return empty if not matched
};

export const formatNameParticipant = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str?.charAt(0)?.toUpperCase() + str?.slice(1)?.toLowerCase();
};

export const generateMapColors = (count) => {
  const palette = [
    '#EF4444', // red
    '#3B82F6', // blue
    '#22C55E', // green
    '#EAB308', // yellow
    '#8B5CF6', // violet
    '#F97316', // orange
    '#EC4899', // pink
    '#14B8A6', // teal
    '#6366F1', // indigo
    '#84CC16', // lime
    '#F43F5E', // rose
    '#0EA5E9', // sky blue
  ];

  const step = 3; // key trick (skip nearby colors)

  return Array.from({ length: count }, (_, i) => {
    return palette[(i * step) % palette.length];
  });
};

// return all module keys allowed for a pillar
export const getAllowedModulesByPillar = (pillars, pillarKey) => {
  const pillar = pillars.find((p) => p.key?.toLowerCase() === pillarKey?.toLowerCase());
  if (!pillar) return [];

  return pillar.modules?.map((m) => m.key) || [];
};

export const getResearchDataPermissions = (permissions) => {
  const research = permissions.find((p) => p.key === 'research');

  // Get permissions from both dashboard and data_collection modules
  const dashboardModule = research?.modules?.find((m) => m.key === 'dashboard');
  const dataModule = research?.modules?.find((m) => m.key === 'data_collection');

  const dashboardKeys = dashboardModule?.permissions?.map((perm) => perm.key) || [];
  const dataKeys = dataModule?.permissions?.map((perm) => perm.key) || [];

  return { dashboardKeys, dataKeys };
};

export const getInitials = (name = '') => {
  if (!name) return '?';

  const parts = name.trim().split(/\s+/);

  // If first and last name exist
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }

  // Only one name → take first 2 letters
  return parts[0].slice(0, 2).toUpperCase();
};

export const formatEventDateTime = (datetime) => {
  if (!datetime) return { date: '-', time: '-' };

  const dateObj = new Date(datetime);

  const date = dateObj.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  });

  const time = dateObj.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  return { date, time };
};

export const donutOptions = (dataArr, left = '30%') => {
  const safeData = Array.isArray(dataArr) ? dataArr.filter((item) => Number(item?.value) > 0) : [];
  const hasData = safeData.length > 0;

  return {
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)', show: hasData },
    legend: {
      show: hasData,
      type: 'scroll',
      orient: 'horizontal',
      bottom: 0,
      left: 'center',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: {
        fontSize: 12,
        color: '#000000',
        fontWeight: 500,
      },
      formatter: (name) => {
        const item = safeData.find((d) => d.name?.toLowerCase() === name?.toLowerCase());
        const value = item ? item.value : 0;

        return `${name} (${value})`;
      },
    },
    series: [
      {
        type: 'pie',
        radius: ['40%', '60%'],
        center: ['50%', '45%'],
        minAngle: 5,
        avoidLabelOverlap: false,
        label: { show: false },
        emphasis: {
          scale: true, // keep zoom effect
          itemStyle: {
            opacity: 1, //  prevent fading
            shadowBlur: 10,
            shadowColor: 'rgba(0,0,0,0.15)',
          },
        },
        labelLine: { show: false },
        color: hasData ? undefined : ['#E5E7EB'],
        data: hasData ? safeData : [{ value: 1, name: 'No data' }],
      },
    ],
    graphic: !hasData
      ? [
          {
            type: 'text',
            left: 'center',
            top: '45%',
            style: {
              text: 'No Data',
              fill: '#9CA3AF',
              fontSize: 13,
              fontWeight: 500,
            },
          },
        ]
      : [],
  };
};

export const chartOptions = (
  xValue,
  yValue,
  xName = 'x Axis',
  yName = 'Y Axis',
  barColors = null
) => {
  const safeX = Array.isArray(xValue) ? xValue : [];
  const safeY = Array.isArray(yValue) ? yValue : [];
  const hasData = safeY.some((value) => Number(value) > 0);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      show: hasData,
      formatter: (params) => {
        return params.map((p) => `${formatWord(p.name)} : ${p.value}`).join('<br/>');
      },
    },

    grid: { left: '12%', right: '5%', bottom: '22%', top: '5%', containLabel: true },
    xAxis: {
      name: xName,
      nameLocation: 'middle',
      nameGap: 40,
      nameTextStyle: {
        color: '#000',
        fontSize: 14,
        fontWeight: 500,
      },
      axisLabel: {
        interval: 0,
        color: '#000',
        rotate: 0,
        fontSize: 12,
      },
      type: 'value',
      min: hasData ? undefined : 0,
      max: hasData ? undefined : 100,
      axisLine: { show: true, lineStyle: { color: '#E0E0E0' } },
      axisTick: { show: false },
      splitLine: { show: true, lineStyle: { type: 'dashed' } },
    },
    yAxis: {
      type: 'category',
      data: safeX.length ? safeX : ['No data'],
      name: yName,
      nameLocation: 'middle',
      nameGap: 120,
      nameTextStyle: {
        color: '#000',
        fontSize: 14,
        fontWeight: 500,
      },
      axisLabel: {
        interval: 0,
        color: '#000',
        rotate: 0,
        fontSize: 12,
        formatter: (value) => {
          const formatted = formatWord(value);
          return formatted.length > 15 ? formatted.slice(0, 10) + '...' : formatted;
        },
      },
    },
    dataZoom: [
      {
        type: 'inside',
        yAxisIndex: 0,
        start: 0,
        end: yValue?.length > 10 ? (10 / yValue.length) * 100 : 100,
        zoomOnMouseWheel: false, // disable zoom
        moveOnMouseMove: true, // drag to scroll
        moveOnMouseWheel: true, //scroll wheel to move
      },
    ],
    series: [
      ...(hasData
        ? [
            {
              name: 'Count',
              type: 'bar',
              data: barColors
                ? safeY.map((value, index) => ({
                    value,
                    itemStyle: {
                      color: barColors[index % barColors.length],
                      borderRadius: [0, 4, 4, 0],
                    },
                  }))
                : safeY,
              itemStyle: barColors ? undefined : { color: '#A7F3D0', borderRadius: [0, 4, 4, 0] },
              barWidth: '40%',
              label: { show: true, position: 'right', color: '#6B7280', fontSize: 10 },
            },
          ]
        : []),
    ],
    graphic: !hasData
      ? [
          {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: {
              text: 'No Data',
              fill: '#9CA3AF',
              fontSize: 14,
              fontWeight: 500,
            },
          },
        ]
      : [],
  };
};

export const chartOptionsAutoHeight = (
  xValue,
  yValue,
  xName = 'x Axis',
  yName = 'Y Axis',
  nameGap = 80,
  yLabel = 16,
  isTextLong = false
) => {
  const safeX = Array.isArray(xValue) ? xValue : [];
  const safeY = Array.isArray(yValue) ? yValue : [];
  const hasData = safeY.some((value) => Number(value) > 0);

  const formatYLabel = (value) => {
    // strip "(Sub Text)" using split — no regex backtracking risk
    const stripped = value.split('(')[0].trim();

    if (stripped.length <= 20) return stripped;

    const words = stripped.split(/\s+/);
    if (words.length > 2) {
      return words.map((w) => w[0].toUpperCase()).join('');
    }

    return stripped.slice(0, yLabel) + '...';
  };

  // Chart tooltips (axis label tooltips are handled separately by ChartCard if isTextLong)
  const tooltipConfig = {
    trigger: 'axis',
    axisPointer: { type: 'shadow' },
    show: hasData,
    formatter: (params) => {
      return params.map((p) => `${p.name} : ${p.value}`).join('<br/>');
    },
  };

  return {
    tooltip: tooltipConfig,
    _yAxisFullData: isTextLong ? safeX : undefined,
    grid: { left: '12%', right: '5%', bottom: '28%', top: '5%', containLabel: true },
    xAxis: {
      name: hasData ? xName : '',
      nameLocation: 'middle',
      nameGap: 40,
      nameTextStyle: { color: '#000', fontSize: 14, fontWeight: 500 },
      axisLabel: { show: hasData, interval: 0, color: '#000', rotate: 0, fontSize: 12 },
      type: 'value',
      min: 0,
      max: hasData ? undefined : 1,
      axisLine: { show: hasData, lineStyle: { color: '#E0E0E0' } },
      axisTick: { show: false },
      splitLine: { show: hasData, lineStyle: { type: 'dashed' } },
    },
    yAxis: {
      type: 'category',
      triggerEvent: isTextLong,
      name: hasData ? yName : '',
      nameLocation: 'middle',
      nameGap: nameGap,
      nameTextStyle: { color: '#000', fontSize: 14, fontWeight: 500 },
      axisLabel: {
        show: hasData,
        interval: 0,
        color: '#000',
        rotate: 0,
        fontSize: 12,
        formatter: formatYLabel,
      },
      data: hasData ? safeX : [],
      axisLine: { show: hasData, lineStyle: { color: '#E0E0E0' } },
      axisTick: { show: false },
    },
    series: hasData
      ? [
          {
            name: 'Count',
            type: 'bar',
            data: safeY,
            itemStyle: { color: '#A7F3D0', borderRadius: [0, 4, 4, 0] },
            barWidth: '40%',
            label: { show: true, position: 'right', color: '#6B7280', fontSize: 10 },
          },
        ]
      : [],
    graphic: !hasData
      ? [
          {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: {
              text: 'No Data',
              textAlign: 'center',
              textVerticalAlign: 'middle',
              fill: '#9CA3AF',
              fontSize: 14,
              fontWeight: 500,
            },
          },
        ]
      : [],
  };
};

export const formatWord = (str) => {
  if (typeof str !== 'string') return str;

  return str
    .replace(/_/g, ' ') // snake_case → space
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase()); // capitalize each word
};

export const normalizeMapData = ({
  data = [],
  categories = [],
  values = [],
  type = 'direct', // 'direct' | 'category'
} = {}) => {
  //  Case 1: Already structured data
  if (type === 'direct') {
    return (data || [])
      .filter((item) => item?.name)
      .map((item) => ({
        name: item.name,
        count: Number(item?.participantCount || item?.count || 0),
      }));
  }

  //  Case 2: categories + values format
  if (type === 'category') {
    return (categories || [])
      .map((name, index) => ({
        name,
        count: Number(values[index] || 0),
      }))
      .filter((item) => item.name && item.name !== 'NA');
  }

  return [];
};

export const linearBarGraph = ({
  leftLabel = 'Left',
  rightLabel = 'Right',
  leftValue = 0,
  rightValue = 0,
  leftColor = ['#fab3ef', '#fab3ef'],
  rightColor = ['#b4f8d7', '#b4f8d7'],
  leftPercent = 0,
  rightPercent = 0,
  title = 'Data',
}) => {
  const total = leftValue + rightValue;
  const hasData = total > 0;

  return {
    xAxis: {
      type: 'value',
      show: false,
      max: total || 1,
    },
    yAxis: {
      type: 'category',
      show: false,
      data: [''],
    },

    series: hasData
      ? [
          {
            name: leftLabel,
            type: 'bar',
            stack: 'total',
            data: [leftValue],
            barWidth: '100%',
            itemStyle: {
              borderRadius: [8, 0, 0, 8],
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 1,
                y2: 0,
                colorStops: [
                  { offset: 0, color: leftColor[0] },
                  { offset: 1, color: leftColor[1] },
                ],
              },
            },
            label: {
              show: true,
              position: 'insideLeft',
              formatter: (params) => `${params.value} (${leftPercent.toFixed(1)}%)`,
              color: '#000',
              fontSize: 12,
            },
          },

          {
            name: rightLabel,
            type: 'bar',
            stack: 'total',
            data: [rightValue],
            barWidth: '100%',
            itemStyle: {
              borderRadius: [0, 8, 8, 0],
              color: {
                type: 'linear',
                x: 1,
                y: 0,
                x2: 0,
                y2: 0,
                colorStops: [
                  { offset: 0, color: rightColor[0] },
                  { offset: 1, color: rightColor[1] },
                ],
              },
            },
            label: {
              show: true,
              position: 'insideRight',
              formatter: (params) => `${params.value} (${rightPercent.toFixed(1)}%)`,
              color: '#000',
              fontSize: 12,
            },
          },
        ]
      : [],

    tooltip: {
      trigger: 'item',
      show: hasData,
      formatter: (params) => {
        const percent = params.seriesName === leftLabel ? leftPercent : rightPercent;

        return `
          <div style="font-weight:600;margin-bottom:4px;">${title}</div>
          ${params.seriesName}: <b>${params.value}</b> (${percent.toFixed(1)}%)
        `;
      },
    },

    grid: {
      left: '2%',
      right: '2%',
      top: '2%',
      bottom: '2%',
    },
    graphic: !hasData
      ? [
          {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: {
              text: 'No Data',
              fill: '#9CA3AF',
              fontSize: 14,
              fontWeight: 500,
            },
          },
        ]
      : [],
  };
};

export const lineChartOptions = (categories = [], values = [], xTitleGap = 40) => {
  const safeCategories = Array.isArray(categories) ? categories : [];
  const safeValues = Array.isArray(values) ? values : [];
  const hasData = safeValues.some((value) => Number(value) > 0);

  return {
    tooltip: {
      trigger: 'axis',
      show: hasData,
      formatter: (params) => {
        const label = params[0]?.axisValue || '';
        const value = params[0]?.data || '';
        return `${label}<br/>Count: ${value}`;
      },
    },

    grid: {
      left: '10%',
      right: '5%',
      bottom: '20%', // extra space for rotated labels
      top: '10%',
      containLabel: true,
    },

    xAxis: {
      type: 'category',
      data: safeCategories.length ? safeCategories : ['No data'],
      name: 'Module',
      nameLocation: 'middle',
      nameGap: xTitleGap,
      nameTextStyle: {
        color: '#000',
        fontSize: 14,
        fontWeight: 500,
      },
      axisLine: { lineStyle: { color: '#E0E0E0' } },
      axisTick: { show: false },

      axisLabel: {
        interval: 'auto',
        hideOverlap: true,
        color: '#333',
        fontSize: 11,
        rotate: 45, // Fixed rotation angle
        formatter: (value) =>
          value && value.length > 10 && safeCategories.length ? value.slice(0, 10) + '...' : value,
      },

      splitLine: {
        show: true,
        interval: 0,
        lineStyle: {
          type: 'dotted',
          color: '#D1D5DB',
        },
      },
    },

    yAxis: {
      type: 'value',
      min: hasData ? undefined : 0,
      max: hasData ? undefined : 100,
      name: 'Locations',
      nameLocation: 'middle',
      nameGap: 40,
      nameTextStyle: {
        color: '#000',
        fontSize: 14,
        fontWeight: 500,
      },
      axisLabel: {
        color: '#000',
        fontSize: 12,
      },
      axisLine: { lineStyle: { color: '#E0E0E0' } },
      axisTick: { show: false },
      splitLine: { lineStyle: { type: 'dashed' } },
    },

    series: [
      ...(hasData
        ? [
            {
              data: safeValues,
              type: 'line',
              symbol: 'circle',
              symbolSize: 8,
              lineStyle: {
                width: 3,
                color: '#6366F1',
              },
              itemStyle: {
                color: '#6366F1',
              },
            },
          ]
        : []),
    ],
    graphic: !hasData
      ? [
          {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: {
              text: 'No Data',
              fill: '#9CA3AF',
              fontSize: 14,
              fontWeight: 500,
            },
          },
        ]
      : [],
  };
};

// Donut data as objects with predefined keys for name and value
export const mapToDonutSimple = (obj = {}, config = [], colors = []) => {
  return config.map((item, index) => ({
    value: Number(obj?.[item.key]) || 0,
    name: item.label,
    itemStyle: { color: colors[index % colors.length] },
  }));
};

// Donut data as array of objects with predefined key for label and value
export const mapToDonut = (keyName, labelName, colors = [], arr = []) => {
  return arr.map((item, index) => ({
    value: Number(item?.count) || 0,
    name: formatLabel(item?.[keyName] || labelName),
    itemStyle: { color: colors[index % colors.length] },
  }));
};

export const generateColors = (count) => {
  const colors = [];
  for (let i = 0; i < count; i++) {
    const hue = 320 - i * (220 / count);
    colors.push(`hsl(${hue}, 70%, 80%)`);
  }
  return colors;
};

export const getResearchTimelineChartOptions = (timeline, selectedYearIndex) => {
  const { years = [], rissGroups: groups = [], series: seriesData = [] } = timeline;

  if (!years.length || !groups.length || !seriesData.length) {
    return {
      tooltip: { show: false },
      legend: { show: false },
      grid: { left: '5%', right: '4%', top: '60px', bottom: '15%', containLabel: true },
      xAxis: {
        type: 'category',
        data: [],
        name: 'RISS Group',
        nameLocation: 'middle',
        nameGap: 40,
        nameTextStyle: { color: '#6B7280', fontSize: 11 },
        axisLine: { show: true, lineStyle: { color: '#D1D5DB', width: 1 } },
        axisTick: { show: false },
        axisLabel: { color: '#6B7280', fontSize: 11 },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        name: 'Farm Count',
        nameTextStyle: { color: '#6B7280', fontSize: 11 },
        axisLine: { show: true, lineStyle: { color: '#D1D5DB', width: 1 } },
        axisTick: { show: false },
        axisLabel: { color: '#6B7280', fontSize: 11 },
        splitLine: { show: true, lineStyle: { color: '#E5E7EB', width: 1, type: 'solid' } },
        minInterval: 1,
      },
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: 'middle',
          style: { text: 'No Data', fill: '#9CA3AF', fontSize: 14, fontWeight: 500 },
        },
      ],
      series: [],
    };
  }

  const colorMap = {
    'Lefa Core': '#FF6B9D',
    'Lefa Check': '#4DD4AC',
    'Lefa Primary': '#6B8AFF',
    LFD: '#A8D5FF',
  };

  const sortedGroups = [...groups].sort(
    (a, b) =>
      parseInt(a?.name?.replace(/\D/g, '') || '0') - parseInt(b?.name?.replace(/\D/g, '') || '0')
  );

  const xAxisData = sortedGroups.map((g) => g?.name ?? '-');

  const series = seriesData.map((program) => ({
    name: program?.name ?? '',
    type: 'line',
    smooth: false,
    data: sortedGroups.map((group) => {
      const programData = program?.data?.find((d) => d.rissGroupId === group.id);
      return programData?.values?.[selectedYearIndex] ?? 0;
    }),
    lineStyle: { width: 2.5 },
    itemStyle: {
      color: colorMap[program?.name] || '#FF6B9D',
      borderWidth: 2,
    },
    symbol: 'circle',
    symbolSize: 8,
    emphasis: { scale: true, scaleSize: 12 },
  }));

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'line',
        lineStyle: {
          color: '#ccc',
          type: 'dashed',
        },
      },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#ddd',
      borderWidth: 1,
      textStyle: {
        color: '#000',
      },
      formatter: (params) => {
        if (!params?.length) return '';
        const year = years[selectedYearIndex] || 'N/A';
        let result = `<b style="font-size: 13px;">${params[0].axisValueLabel}</b><br/>`;
        result += `<span style="color: #666; font-size: 11px;">Year: ${year}</span><br/>`;
        params.forEach((param) => {
          if (param.value > 0) {
            result += `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${param.color};margin-right:5px;"></span>${param.seriesName}: <b>${param.value}</b><br/>`;
          }
        });
        return result;
      },
    },

    legend: {
      data: seriesData.map((s) => s?.name ?? ''),
      top: 5,
      right: 10,
      itemWidth: 15,
      itemHeight: 10,
      itemGap: 15,
      textStyle: {
        fontSize: 12,
        color: '#000',
      },
      icon: 'circle',
    },

    grid: {
      left: '5%',
      right: '4%',
      top: '60px',
      bottom: '15%',
      containLabel: true,
    },

    xAxis: {
      type: 'category',
      data: xAxisData,
      name: 'RISS Group',
      nameLocation: 'middle',
      nameGap: 40,
      nameTextStyle: {
        color: '#6B7280',
        fontSize: 11,
      },
      boundaryGap: false,
      axisLine: {
        show: true,
        lineStyle: {
          color: '#D1D5DB',
          width: 1,
        },
      },
      axisTick: {
        show: true,
        alignWithLabel: true,
        lineStyle: {
          color: '#D1D5DB',
        },
      },
      axisLabel: {
        color: '#6B7280',
        fontSize: 11,
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: '#E5E7EB',
          width: 1,
          type: 'solid',
        },
      },
    },

    yAxis: {
      type: 'value',
      name: 'Farm Count',
      nameTextStyle: {
        color: '#6B7280',
        fontSize: 11,
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: '#D1D5DB',
          width: 1,
        },
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        color: '#6B7280',
        fontSize: 11,
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: '#E5E7EB',
          width: 1,
          type: 'solid',
        },
      },
      minInterval: 1,
    },

    series,
  };
};

export const multiBarOptions = (categories = [], series = [], yTitleGap = 120) => {
  const safeCategories = Array.isArray(categories) ? categories : [];
  const safeSeries = Array.isArray(series) ? series : [];
  const hasData = safeSeries.some(
    (item) => Array.isArray(item?.data) && item.data.some((value) => Number(value) > 0)
  );

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      show: hasData,

      textStyle: {
        fontSize: 10, // change size here
        color: '#000',
      },

      backgroundColor: '#fff', // optional (clean UI)
      borderColor: '#E5E7EB',
      borderWidth: 1,
      extraCssText: 'box-shadow: 0 2px 8px rgba(0,0,0,0.1);',
    },

    legend: {
      show: hasData,
      bottom: 0,
      type: 'scroll',
      textStyle: { fontSize: 10 },
    },
    grid: {
      left: '10%',
      right: '8%',
      top: '5%',
      bottom: 75,
      containLabel: true,
    },

    xAxis: {
      name: 'Farm Count →',
      nameLocation: 'middle',
      nameGap: 35, // ✅ increase this
      nameTextStyle: {
        color: '#000',
        fontSize: 14,
        fontWeight: 500,
        padding: [10, 0, 0, 0], // ✅ push title down
      },
      axisLabel: {
        interval: 0,
        color: '#000',
        rotate: 0,
        fontSize: 12,
      },
      type: 'value',
      min: hasData ? undefined : 0,
      max: hasData ? undefined : 100,
      axisLine: { show: true, lineStyle: { color: '#E0E0E0' } },
      axisTick: { show: false },
      splitLine: { show: true, lineStyle: { type: 'dashed' } },
    },

    yAxis: {
      type: 'category',
      name: 'Data Category',
      nameLocation: 'middle',
      nameGap: yTitleGap, // ✅ increased to fit longer labels
      nameTextStyle: {
        color: '#000',
        fontSize: 14,
        fontWeight: 500,
      },
      axisLabel: {
        interval: 0,
        color: '#000',
        rotate: 0,
        fontSize: 11,
        formatter: (value) => value, // ✅ show full label
      },
      data: safeCategories.length ? safeCategories : ['No data'],
      axisLine: { show: true, lineStyle: { color: '#E0E0E0' } },
      axisTick: { show: false },
    },
    series: hasData
      ? safeSeries.map((s, i) => ({
          name: s.name.replace('_', ' '),
          type: 'bar',
          data: s.data,
          barWidth: 6,
          barGap: '80%', // space between series
          barCategoryGap: '80%',
          itemStyle: {
            color: GAUGE_COLORS[i % GAUGE_COLORS.length],
            borderRadius: [0, 3, 3, 0],
          },

          label: {
            show: true,
            position: 'right',
            fontSize: 10,
            formatter: (p) => (p.value > 2 ? p.value : ''), // 👈 hide small values
          },
        }))
      : [],
    graphic: !hasData
      ? [
          {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: {
              text: 'No Data',
              fill: '#9CA3AF',
              fontSize: 14,
              fontWeight: 500,
            },
          },
        ]
      : [],
  };
};
